import { createApi } from '@reduxjs/toolkit/query/react';

import {
  SendoutsEndpoints as Endpoints,
  type CreateSendoutRequest,
  type CreateSendoutResponse,
  type EditSendoutRequest,
  type EditSendoutResponse,
  type GetSendoutByIdRequest,
  type GetSendoutByIdResponse,
  type GetSendoutClientsRequest,
  type GetSendoutClientsResponse,
  type GetSendoutDetailsRequest,
  type GetSendoutDetailsResponse,
  type GetSendoutFileRequest,
  type GetSendoutFileResponse,
  type GetSendoutFilterDataRequest,
  type GetSendoutFilterDataResponse,
  type GetSendoutForEditRequest,
  type GetSendoutForEditResponse,
  type GetSendoutKeywordsRequest,
  type GetSendoutKeywordsResponse,
  type GetSendoutsRequest,
  type GetSendoutsResponse,
  type PrepareSendoutCreationRequest,
  type PrepareSendoutCreationResponse,
  type PreviewSendoutMessagesRequest,
  type PreviewSendoutMessagesResponse,
  type StartSendoutRequest,
  type StartSendoutResponse,
  type StopSendoutRequest,
  type StopSendoutResponse,
} from '@app/api/Sendouts/types';
import { createApiBaseQuery, Methods } from '@app/api/shared';
import { SENDOUTS_CHUNK } from '@app/constants';

export const sendoutsApi2 = createApi({
  reducerPath: 'sendoutsApi2',
  baseQuery: createApiBaseQuery(SENDOUTS_CHUNK),
  endpoints: ({ mutation, query }) => ({
    [Endpoints.GetSendoutById]: query<
      GetSendoutByIdResponse,
      GetSendoutByIdRequest
    >({
      query: (params) => ({
        url: Endpoints.GetSendoutById,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetSendouts]: query<GetSendoutsResponse, GetSendoutsRequest>({
      query: (params) => ({
        url: Endpoints.GetSendouts,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.PrepareSendoutCreation]: query<
      PrepareSendoutCreationResponse,
      PrepareSendoutCreationRequest
    >({
      query: (params) => ({
        url: Endpoints.PrepareSendoutCreation,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.CreateSendout]: mutation<
      CreateSendoutResponse,
      CreateSendoutRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.CreateSendout,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.StartSendout]: mutation<
      StartSendoutResponse,
      StartSendoutRequest
    >({
      query: (params) => ({
        url: Endpoints.StartSendout,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.StopSendout]: mutation<StopSendoutResponse, StopSendoutRequest>({
      query: (params) => ({
        url: Endpoints.StopSendout,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.GetSendoutFilterData]: query<
      GetSendoutFilterDataResponse,
      GetSendoutFilterDataRequest
    >({
      query: (params) => ({
        url: Endpoints.GetSendoutFilterData,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.PreviewSendoutMessages]: mutation<
      PreviewSendoutMessagesResponse,
      PreviewSendoutMessagesRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.PreviewSendoutMessages,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetSendoutDetails]: query<
      GetSendoutDetailsResponse,
      GetSendoutDetailsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetSendoutDetails,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetSendoutClients]: query<
      GetSendoutClientsResponse,
      GetSendoutClientsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetSendoutClients,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetSendoutFile]: query<
      GetSendoutFileResponse,
      GetSendoutFileRequest
    >({
      query: (params) => ({
        url: Endpoints.GetSendoutFile,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetSendoutKeywords]: query<
      GetSendoutKeywordsResponse,
      GetSendoutKeywordsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetSendoutKeywords,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetSendoutForEdit]: query<
      GetSendoutForEditResponse,
      GetSendoutForEditRequest
    >({
      query: (params) => ({
        url: Endpoints.GetSendoutForEdit,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.EditSendout]: mutation<EditSendoutResponse, EditSendoutRequest>({
      query: ({ data, ...params }) => ({
        url: Endpoints.EditSendout,
        method: Methods.PUT,
        params,
        data,
      }),
    }),
  }),
});

export const {
  useGetSendoutByIdQuery,
  useLazyGetSendoutByIdQuery,
  useGetSendoutsQuery,
  useLazyGetSendoutsQuery,
  usePrepareSendoutCreationQuery,
  useLazyPrepareSendoutCreationQuery,
  useCreateSendoutMutation,
  useStartSendoutMutation,
  useStopSendoutMutation,
  useGetSendoutFilterDataQuery,
  useLazyGetSendoutFilterDataQuery,
  usePreviewSendoutMessagesMutation,
  useGetSendoutDetailsQuery,
  useLazyGetSendoutDetailsQuery,
  useGetSendoutClientsQuery,
  useLazyGetSendoutClientsQuery,
  useGetSendoutFileQuery,
  useLazyGetSendoutFileQuery,
  useGetSendoutKeywordsQuery,
  useLazyGetSendoutKeywordsQuery,
  useGetSendoutForEditQuery,
  useLazyGetSendoutForEditQuery,
  useEditSendoutMutation,
} = sendoutsApi2;
